import {ReactNode} from 'react';
import {CookieSetOptions} from 'universal-cookie';
import {Document} from '@contentful/rich-text-types';
import {IntlShape} from 'react-intl';
import {AddressType} from '../components/DesignSystem/Fields/Address/AddressType';
import {Language} from '../localeUtils/getLanguageFromLocale';
import {EmptyStringValue} from './utilities';
import {Locale} from '../RootProvider/LocaleContext';

export enum Alignment {
  LEFT = 'alignLeft',
  CENTER = 'alignCenter',
  RIGHT = 'alignRight',
}

export enum RadioYesNoField {
  YES = 'yes',
  NO = 'no',
}

export enum IdentityChallenge {
  NEW_PASSWORD_REQUIRED = 'NEW_PASSWORD_REQUIRED',
}

export enum Priority {
  PRIORITY_NL_LOWSTARTER = 14,
  PRIORITY_NL_SDR = 13,
  PRIORITY_NL_LOW_BFS = 12,
  PRIORITY_NL_BFS = 11,
  PRIORITY_BROKER_LOW = 10,
  PRIORITY_LOW = 9,
  PRIORITY_MEDIUM_LOW = 8,
  PRIORITY_MEDIUM_HIGH = 7,
  PRIORITY_HIGH = 6,
}

export enum SalesQueue {
  SALES_QUEUE_LOW = 1,
  SALES_QUEUE_MEDIUM_LOW = 2,
  SALES_QUEUE_MEDIUM_HIGH = 3,
  SALES_QUEUE_HIGH = 4,
  SALES_QUEUE_BRS = 5,
  SALES_QUEUE_PROPERTY_LOW = 6,
  SALES_QUEUE_PROPERTY_HIGH = 7,
  SALES_QUEUE_ASSET_HIGH = 8,
  SALES_QUEUE_ASSET_LOW = 9,
  SALES_QUEUE_SOLE_TRADER_HIGH = 10,
  SALES_QUEUE_SOLE_TRADER_MID = 11,
  SALES_QUEUE_BROKER_ONLY_HIGH = 12,
  SALES_QUEUE_BROKER_LOW = 13,
  SALES_QUEUE_NL_LOWSTARTER = 14,
  SALES_QUEUE_NL_SDR = 15,
  SALES_QUEUE_NL_LOW_BFS = 16,
  SALES_QUEUE_NL_BFS = 17,
  SALES_QUEUE_REVENUE_HIGH = 18,
  SALES_QUEUE_STARTUP_LOANS = 21,
  SALES_QUEUE_CCJ_NON_HOMEOWNER = 22,
  SALES_QUEUE_STARTUP_CCJ_NON_HOMEOWNER = 23,
  SALES_QUEUE_BRIDGING_LOAN = 24,
}

export const SalesQueuesWithProductPage = [
  SalesQueue.SALES_QUEUE_LOW,
  SalesQueue.SALES_QUEUE_PROPERTY_LOW,
  SalesQueue.SALES_QUEUE_ASSET_LOW,
  SalesQueue.SALES_QUEUE_BROKER_LOW,
  SalesQueue.SALES_QUEUE_NL_LOWSTARTER,
  SalesQueue.SALES_QUEUE_NL_SDR,
  SalesQueue.SALES_QUEUE_NL_LOW_BFS,
  SalesQueue.SALES_QUEUE_NL_BFS,
];

export type ApplicationIdObject = {
  applicationId: string;
  sales_queue: SalesQueue;
  priority: Priority;
};

export interface ApplicationWithProducts extends Application {
  products: Product[];
}

export enum PropertyFinanceType {
  REFINANCING = 'refinancing',
  DEVELOPING = 'developing',
  PURCHASING = 'purchasing',
}

export enum AssetCategoryType {
  CONSTRUCTION = 'construction',
  ENERGY = 'energy',
  EXCAVATION = 'excavation',
  FORKLIFTS = 'forklifts',
  AGRICULTURAL_MACHINERY = 'agricultural_machinery',
  MARITIME = 'maritime',
  RECYCLING = 'recycling',
  TRANSPORT = 'transport',
  CLEANING_MACHINES = 'cleaning_machines',
  OTHER = 'other',
}

export enum AssetFinanceType {
  REFINANCING = 'refinancing',
  PURCHASING = 'purchasing',
}

// NB - there are two different LegalStatus enums in the codebase. One for Prop form and a second for Quote form
// this one is for the Quote Form
export enum LegalStatus {
  LIMITED_COMPANY = 'limited_company',
  LIMITED_PARTNERSHIP = 'limited_partnership',
  ORDINARY_PARTNERSHIP = 'ordinary_partnership',
  PUBLIC_LIMITED = 'public_limited',
  SOLE_TRADER = 'sole_trader',
  PROPRIETORSHIP = 'proprietorship',
  NAMELESS_SOCIETY = 'nameless_society',
  GENERAL_PARTNERSHIP_VOF = 'general_partnership_vof',
  PRIVATE_COMPANY = 'private_company',
  OTHER = 'other',
}

export enum FinancePurpose {
  ACQUISITION = 'acquisition',
  ASSET_FINANCE = 'asset_finance',
  BILL_OR_TAX_PAYMENT = 'bill_or_tax_payment',
  BRIDGING = 'bridging',
  BUSINESS_STARTUP = 'business_startup',
  BUYOUT = 'buyout',
  BUY_IN_OR_OUT = 'buyin_or_buyout',
  DEBT_REFINANCE = 'debt_refinance',
  GROWTH_CAPITAL = 'growth_capital',
  OTHER = 'Other',
  PROPERTY_PURCHASE = 'property_purchase',
  WORKING_CAPITAL = 'working_capital',
}

export enum TermLength {
  MONTHS = 1,
  YEARS = 12,
  MANY_YEARS = 60,
  UNKNOWN = 0,
}

export enum Industry {
  AGRICULTURE_FORESTRY_FISHING = 'AFF',
  ARCHITECTURE_ENGINEERING = 'AE',
  BEAUTY_CARE = 'BV',
  BUSINESS_SUPPORT_SERVICES = 'BSS',
  CAR_DEALERS_GARAGES_MECHANICS = 'CDM',
  CLEANING_LANDSCAPING_GARDENING = 'CLG',
  CONSTRUCTION = 'CON',
  E_COMMERCE = 'EC',
  EDUCATION_TRAINING = 'ET',
  FINANCE_INSURANCE = 'FI',
  HEALTH_CARE = 'HC',
  HOTELS_RESTAURANTS_PUBS = 'HRP',
  IT_COMMUNICATIONS = 'ITC',
  LEISURE_OTHER_SERVICES = 'LO',
  MANUFACTURING_FOOD_PROCESSING_PRINTING = 'MFP',
  OTHER = 'OTH',
  PROFESSIONAL_SERVICES = 'PS',
  PROPERTY_REAL_ESTATE = 'PRE',
  PUBLISHING_FILM_MEDIA_PRODUCTION = 'PFM',
  REAL_ESTATE = 'PRE',
  RETAIL = 'RET',
  SPORTS_AND_RECREATION = 'SR',
  TRANSPORT_LOGISTICS = 'TL',
  WHOLESALE = 'WHL',
}

export enum Role {
  DIRECTOR = 'D',
  SHAREHOLDER = 'S',
  OWNER = 'OW',
  COLLABORATOR = 'C',
  EXTERNAL_CONSULTANT = 'EC',
  ACCOUNTANT = 'AC',
  OTHER = 'O',
}

export enum ResidentialStatus {
  TENANT = 'tenant',
  OWNER_WITH_MORTGAGE = 'owner_with_mortgage',
  OWNER_NO_MORTGAGE = 'owner_no_mortgage',
  RENT_FREE = 'rent_free',
}

// QuoteFormV1
export interface QuoteFormV1Values {
  asset_finance_type: string;
  business_name: string;
  capital_type: string;
  company_number: string;
  email: string;
  finance_purpose: FinancePurpose;
  first_name: string;
  legal_status: LegalStatus;
  living_in_the_property: string;
  other_finance_purpose: string;
  outstanding_invoices: number;
  owns_property: string;
  privacy_policy_accepted: boolean;
  property_development_type: string;
  property_finance_type: PropertyFinanceType;
  registered: Registered;
  requested_amount: number;
  revenue: number;
  surname: string;
  telephone: string;
  toc_accepted: boolean;
  trading_startdate: string;
  type_of_mortgage: string;
  type_of_property: string;
  when_finance_required: string;
}

// QuoteFormV2
export interface QuoteFormV2Values extends QuoteFormV1Values {
  address: AddressType;
  date_of_birth: string;
  industry: Industry;
  is_green_business: string;
  is_green_asset: string;
  last_12_months_profit: string;
  marketing_opt_in: boolean;
  monthly_expense_amount: string;
  monthly_non_business_income: string;
  monthly_other_household_income: string;
  monthly_turnover: string;
  number_of_dependents: string;
  residential_status: ResidentialStatus;
  role: Role;
  trade_with_restricted_countries: string;

  // nl fields
  asset_category: AssetCategoryType;
  property_market_value: number;
  property_is_known: string;
  property_is_known_address: AddressType;
  asset_purchase_value: number;
  other_asset_category: string;
  acquisition_price: number;
  other_capital_type: string;
  business_plan: string;
  self_investment: number;
  term_length: TermLength;
  number_of_shareholders: string;
  number_of_partners: string;
  registered_business_address: AddressType;
  profitable_in_last_12_months: ProfitableInLast12MonthsValue;
}

type QuoteFormV2ValuesWithEmptyStrings = EmptyStringValue<QuoteFormV2Values>;

export interface QuoteFormValues extends QuoteFormV2ValuesWithEmptyStrings {
  // as it's formik:
  [field: string]: string | boolean | number | AddressType | null;
}

type QuoteFormLiteFormFields = Pick<
  QuoteFormV2Values,
  | 'business_name'
  | 'email'
  | 'finance_purpose'
  | 'first_name'
  | 'marketing_opt_in'
  | 'requested_amount'
  | 'surname'
  | 'telephone'
>;

export interface QuoteFormLiteValues extends EmptyStringValue<QuoteFormLiteFormFields> {
  // as it's formik:
  [field: string]: string | boolean | number | AddressType | null;
}

// object from Customer Application Service
export type Application = {
  affid?: string;
  applicationId?: string;
  asset_finance_type: string;
  business_name: string;
  capital_type: string | null;
  company_number: string;
  email: string;
  foid?: string;
  finance_purpose: string;
  first_name: string;
  landingUrl?: string;
  language: Language;
  legal_status: string;
  months_revenue?: number;
  other_finance_purpose: string;
  outstanding_invoices: number | null;
  owns_property: boolean | null;
  privacy_policy_accepted: boolean;
  property_development_type: string;
  property_finance_type: string;
  priority?: Priority;
  registered?: Registered | boolean | null;
  requested_amount?: number;
  revenue?: number | string;
  surname: string;
  telephone: string;
  toc_accepted: boolean;
  trading_startdate?: string;
  type_of_mortgage: string;
  type_of_property: string;
  when_finance_required: string | null;

  // Quote form v2
  address?: AddressType;
  date_of_birth?: string;
  industry?: Industry;
  is_green_business?: string;
  is_green_asset?: string;
  last_12_months_profit?: string;
  living_in_the_property?: boolean;
  monthly_expense_amount?: string;
  monthly_non_business_income?: string;
  monthly_other_household_income?: string;
  monthly_turnover?: string;
  number_of_dependents?: string;
  need_bridging_loan?: boolean | null;
  password?: string;
  residential_status?: ResidentialStatus;
  role?: Role;
  trade_with_restricted_countries?: boolean;
  formOrigin?: string;

  // nl fields
  asset_category?: AssetCategoryType;
  property_market_value: number;
  property_is_known?: string;
  property_is_known_address?: AddressType;
  asset_purchase_value?: number;
  other_asset_category?: string;
  acquisition_price?: number;
  other_capital_type?: string;
  business_plan?: string;
  self_investment?: number;
  term_length?: TermLength;
  number_of_shareholders?: string;
  number_of_partners?: string;
  registered_business_address?: AddressType;
  profitable_in_last_12_months?: ProfitableInLast12MonthsValue;

  // for selecting products
  selectedLenderName: string;
  selectedProductId: number;
  selectedProductIds?: number[];
};

export type Lender = {
  name: string;
  legal_name: string;
  id: number;
};

export enum ProductCost {
  LO = 'LO',
  ME = 'ME',
  HI = 'HI',
}

export enum ProductSpeed {
  MI = 'MI',
  HO = 'HO',
  DA = 'DA',
  WE = 'WE',
  FO = 'FO',
  MO = 'MO',
}

export type Product = {
  accepts_automatic_proposals: boolean;
  additional_information_link: string;
  can_accept_offer: boolean;
  cost: ProductCost;
  description: string;
  full_name: string;
  id: number;
  lender: Lender;
  lender_required_form_fields_json: string;
  logo: string;
  name: string;
  required_documents: string;
  score: number;
  speed: ProductSpeed;
  zeus_max_cost?: number | null;
  zeus_min_cost?: number | null;
  zeus_speed?: number | null;
  type?: ProductType;
};

export interface ProductCardProduct extends Product {
  features?: SystemOfRecord.Product['features'];
}

export enum ProductType {
  BROKER = 'broker',
  DIGITAL = 'digital',
}

// @todo -- type for the ErrorMessages -- will look something like `Errors<T>`
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Errors = any;

export type Field = {
  name: string;
  required?: boolean;
  key?: string;
  type?: string;
};

export type Cookies = {
  affid?: string;
  foid?: string;
  landingUrl?: string;
};

export type SetCookie = (cookieName: string, cookieValue: string, cookieSettings: CookieSetOptions) => void;

export type LinkProp = {
  href: string;
  label: string;
  testId?: string;
};

export enum Scope {
  ADVISOR = 'advisor',
  CUSTOMER = 'customer',
  SUPPLIER = 'supplier',
}

export type Account = {
  challengeName?: IdentityChallenge;
  email: string;
  emailVerified: boolean;
  jwt?: string;
  scopes?: Scope[];
};

export type User = Account &
  SystemOfRecord.AccountHolderDetails & {
    partner?: SystemOfRecord.PartnerModelAccount;
  };

export enum PartnerType {
  BANK_REFERRAL_SCHEME = 'bank_brs',
  NOT_BANK_REFERRAL_SCHEME = 'bank_non_brs',
  PARTNER_PAY_PER_LEAD = 'partner_ppl',
  PARTNER_REVENUE_SHARE = 'partner_rev_share',
  PARTNER_HYBRID = 'partner_hybrid',
  ADVISORY_ACCOUNT = 'advisory_accountant',
  ADVISORY_BROKER = 'advisory_broker',
  ADVISORY_OTHER = 'advisory_other',
}

export enum PartnerLegalStatus {
  LIMITED_COMPANY = 'limited_company',
  SOLE_TRADER = 'sole_trader',
  LIMITED_PARTNERSHIP = 'limited_partnership',
  PARTNERSHIP_THREE_OR_LESS = 'partnership_with_three_or_fewer',
  PARTNERSHIP_FOUR_OR_MORE = 'partnership_with_four_or_more_partners',
  OTHER = 'other',
}

export enum AdvisorType {}

export enum WhenFinanceRequired {
  ASAP = 'asap',
  NEXT_WEEK = 'next_week',
  SHOPPING_AROUND = 'shopping_around',
}

export enum ApplicationStatus {
  ACCEPTED = 'accepted',
  CLOSED_LOST = 'closed_lost',
  CREDIT_APPROVED = 'credit_approved',
  CUSTOMER_SIGNED = 'customer_signed',
  DRAWNDOWN = 'drawndown',
  FAILED_SUBMISSION = 'failed_submission',
  LENDER_REVIEWING = 'lender_reviewing',
  REJECTED = 'rejected',
  WAITING_FOR_CUSTOMER = 'waiting_for_customer',
  WAITING_FOR_FUNDINGOPTIONS = 'waiting_for_fundingoptions',
  WAITING_FOR_LENDER = 'waiting_for_lender',
}

export enum Registered {
  YES = 'Registered',
  NO = 'notRegistered',
}

export enum ProfitableInLast12MonthsValue {
  YES = 'yes',
  NO = 'no',
  I_DONT_KNOW = 'i_do_not_know',
}

// From Contentful
export interface ContentfulContent {
  raw: string;
  references?: ObjectOfAny[];
}

export interface ContentJson extends Document {
  content: ArrayOfAny;
}

export type Conversion = {
  name: string;
  slug: string;
  node_locale: string;
  description: string;
  content: ContentfulContent;
  hero?: Hero;
  isNoindex: boolean | null;
  accordion?: Accordion;
  showHowDoesItWork: boolean;
  showOurProducts: boolean;
  showLogos: boolean;
};

export type Hero = {
  image: {
    file: {
      url: string;
    };
  };
  title?: string;
  source?: string;
  showCheckpointsOnHero?: boolean;
};

// these must match up with contentful
// added to not allow users to screw this up
export enum PartnerTracking {
  SIMPLY_BUSINESS = 'simplyBusiness',
}

export type Partner = {
  logo: {
    file: {
      url: string;
    };
  };
  slug?: string; // only need it for the purposes of building the page
  name: string;
  affid: string;
  description?: string;
  content?: ContentfulContent;
  hero?: Hero;
  accordion?: Accordion;
  trackingScript?: PartnerTracking;
};

export type Advisor = {
  description: string;
  logo: {
    file: {
      url: string;
    };
  };
  slug?: string; // only need it for the purposes of building the page
  name: string;
  affid: string;
  hero: Hero;
};

// must match strings in contentful
export enum SupplierType {
  ENERGY = 'energy',
  INSURANCE = 'insurance',
}

export type Supplier = {
  hero: Hero;
  logo: {
    title: string;
    file: {
      url: string;
    };
  };
  description: {
    description: string;
  };
  name: string;
  slug: string;
  supplierUrl: string;
  type: SupplierType;
  accordion?: Accordion;
};

/* Customer Stories */
export interface HomePageCustomerStory {
  id: string;
  name: string;
  quote: {
    quote: string;
  };
  image: {
    file: {
      url: string;
    };
  };
  businessName: string;
  slug: string;
}

export interface HubCustomerStory {
  id: string;
  businessName: string;
  name: string;
  node_locale: string;
  displayOnLocale: Locale[];
  slug: string;
  industry: {
    name: string;
    icon: {
      file: {
        url: string;
      };
    };
  };
  loanType: {
    name: string;
  }[];
  quote: {
    quote: string;
  };
  image: {
    file: {
      url: string;
    };
  };
}

export interface CustomerStory {
  id: string;
  businessName: string;
  content: ContentfulContent;
  name: string;
  node_locale: Locale;
  displayOnLocale: Locale[];
  slug: string;
  date: string;
  industry: {
    name: string;
    icon: {
      file: {
        url: string;
      };
    };
  };
  loanType: {
    name: string;
    url: string;
  }[];
  quote: {
    quote: string;
  };
  image: {
    file: {
      url: string;
    };
  };
}

export type CardArticle = {
  name: string;
  shortDescription: string;
  path: string;
  displayOnLocale: Locale[];
  imageUrl: string;
};

/* Blog Types */
export type Author = {
  name?: string;
  jobTitle?: string;
  image?: {
    file: {
      url: string;
    };
  };
};

export type BlogCategory = {
  name: string;
  slug: string;
};

export type BlogArticle = {
  metaName?: string;
  metaDescription?: string;
  metaRobots?: string;
  author?: Author;
  name: string;
  id: string;
  shortDescription: {internal: {content: string}};
  slug: string;
  category: BlogCategory;
  createdAt: string;
  displayOnLocale: Locale[];
  hero?: Hero;
  date?: string | null;
  content?: ContentfulContent;
  articleCardImage?: {
    file: {
      url: string;
    };
  };
};

export type BCAProduct = {
  contentful_id: string;
  node_locale: string;
  displayOnLocale: Locale[];
  accountFee: string;
  openingTime: string;
  bankTransfersFee: string;
  invoicing: string;
  overdraft: string;
  access: string;
  title: string;
  linkToKnowledgePage?: {slug: string};
  description: {internal: {content: string}};
};

export type BCAProvider = {
  contentful_id: string;
  node_locale: string;
  displayOnLocale: Locale[];
  name: string;
  logo: {
    file: {
      url: string;
    };
  };
  product: BCAProduct[];
};

// Contentful Driven Website Single Pages

export type WebsiteSinglePage = {
  title: string;
  slug: string;
  displayOnLocale: Locale[];
  hero?: Hero;
  content: ContentfulContent;
};

// For soft-type checking, use sparingly with comprehensive tests
/* eslint-disable @typescript-eslint/no-explicit-any */
export type ObjectOfAny = {
  [key: string]: any;
};

export type ObjectOfStrings = {
  [key: string]: string;
};

export type ArrayOfAny = any[];

// window extensions
declare global {
  interface Window {
    Sentry: any;
  }
}

export type FormatMessageFunction = IntlShape['formatMessage'];

// Accordion
export type Accordion = {
  items: ContentfulAccordionItem[];
  accordionName: string;
  subheading?: string;
};

// coming in from contentful
export interface ContentfulAccordionItem {
  label: string;
  content: ContentfulContent;
  id: string;
}

// used in our code
export interface AccordionItemProps {
  label: string;
  children: ReactNode;
  id?: string;
}

// custom analytics for Partners
export enum AnalyticsType {
  PIXEL = 'pixel',
  SCRIPT = 'script',
  SCRIPT_SRC = 'scriptSrc',
}

export interface Analytics {
  code: string;
  type: AnalyticsType;
}

export interface CustomAnalytics {
  register: Analytics[];
  leadCreated: Analytics[];
  leadSubmitted: Analytics[];
}

/* eslint-enable @typescript-eslint/no-explicit-any */

// Document upload

export enum ErrorCodes {
  FILE_TOO_LARGE = 'file-too-large',
  TOO_MANY_FILES = 'too-many-files',
  FILE_INVALID_TYPE = 'file-invalid-type',
}

export enum DateFormat {
  FULL_DATE = 'yyyy-mm-dd',
  MONTH_AND_YEAR = 'yyyy-mm',
}
